import React, { useCallback, useMemo, useState } from 'react';
import { grey } from '@mui/material/colors';
import { Button, Skeleton, Stack, Typography } from '@mui/material';
import { EligibleCategory } from '@trnsact/trnsact-shared-types';
import { Listing, Group } from 'components/shared/TableListing';
import { useEligibleFinancePrograms } from '../../../api';
import {
  EligibleFinanceProgramTableRow,
  EligibleFinanceProgramsContext,
} from 'modules/creditSubmission/lenderDeskingPanel/types';
import { buildCriteriaRows, buildScorecardRows } from 'modules/creditSubmission/lenderDeskingPanel/lib';
import { columns } from 'modules/creditSubmission/lenderDeskingPanel/constants';
import { GroupHeader } from './GroupHeader';

interface Props {
  vo: any;
  accountId: string;
  creditSubmissionId: string;
  onApplyProgram: (financeProgramId: string, tierName: string | null) => void;
}

export const EligibleFinanceProgramsSection = ({ vo, accountId, creditSubmissionId, onApplyProgram }: Props) => {
  const { isLoading, eligiblePrograms } = useEligibleFinancePrograms({ accountId, creditSubmissionId });
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const handleSelect = useCallback((id: string) => {
    setSelectedId(prev => (prev === id ? null : id));
  }, []);

  const context = useMemo<EligibleFinanceProgramsContext>(
    () => ({ selectedId, onSelect: handleSelect }),
    [selectedId, handleSelect]
  );

  const groups = useMemo((): Group<EligibleFinanceProgramTableRow>[] => {
    const scorecardPrograms = eligiblePrograms.filter(p => p.eligibleCategory === EligibleCategory.Scorecard);
    const criteriaPrograms = eligiblePrograms.filter(p => p.eligibleCategory === EligibleCategory.Criteria);

    return [
      {
        name: 'scorecard',
        data: buildScorecardRows(scorecardPrograms, vo),
        beforeGroup: {
          render: () => <GroupHeader label="Scorecard Matches (Tiered)" count={scorecardPrograms.length} />,
          cellStyles: { backgroundColor: grey[50] },
        },
      },
      {
        name: 'criteria',
        data: buildCriteriaRows(criteriaPrograms),
        beforeGroup: {
          render: () => <GroupHeader label="Criteria-Based Matches" count={criteriaPrograms.length} />,
          cellStyles: { backgroundColor: grey[50] },
        },
      },
    ];
  }, [eligiblePrograms]);

  const selectedRow = useMemo(() => {
    if (!selectedId) return null;
    for (const group of groups) {
      const found = group.data.find(row => row.id === selectedId);
      if (found) return found;
    }
    return null;
  }, [selectedId, groups]);

  return (
    <Stack gap="0.5rem">
      <Typography variant="h6" component="span">
        Available Programs
      </Typography>
      <Typography variant="body2" color="text.secondary">
        All available programs with scorecard and criteria prioritization
      </Typography>

      {isLoading ? (
        <Stack gap="0.5rem">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
          ))}
          <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} width="20%" />
        </Stack>
      ) : (
        <>
          <Listing<EligibleFinanceProgramTableRow, EligibleFinanceProgramsContext>
            loading={false}
            tableData={[]}
            columns={columns}
            groups={groups}
            injectContext={context}
            staticSettings={{ allGroupsOpen: true }}
          />
          <Stack direction="row" justifyContent="flex-start">
            <Button
              variant="contained"
              size="small"
              disabled={!selectedRow}
              onClick={() => {
                if (selectedRow) {
                  onApplyProgram(selectedRow.financeProgramId, selectedRow.tierName);
                }
              }}
            >
              Apply Program
            </Button>
          </Stack>
        </>
      )}
    </Stack>
  );
};
